import { authResultSchema, refreshSession, } from "@commerce-os/contracts";
import { newId } from "@commerce-os/utils";
import { apiErrorFrom, ApiNetworkError } from "./errors.js";
/** A key for one user action: create it when the action starts and send the same key on every retry. */
export function newIdempotencyKey() {
    return newId();
}
export function createApiClient(options) {
    const fetchFn = options.fetch ?? globalThis.fetch.bind(globalThis);
    const base = options.baseUrl.replace(/\/+$/, "");
    let refreshing;
    async function send(endpoint, input, token) {
        const headers = { accept: "application/json" };
        if (token !== undefined && !("public" in endpoint.access))
            headers["authorization"] = `Bearer ${token}`;
        if (input.authorization !== undefined)
            headers["authorization"] = input.authorization;
        if (input.idempotencyKey !== undefined)
            headers["idempotency-key"] = input.idempotencyKey;
        let body;
        if (endpoint.body !== undefined) {
            headers["content-type"] = "application/json";
            body = JSON.stringify(input.body ?? {});
        }
        try {
            return await fetchFn(base + pathOf(endpoint, input), {
                method: endpoint.method,
                headers,
                body: body ?? null,
                // The refresh cookie (httpOnly, path /v1/auth) must be sent and received cross-origin.
                credentials: "include",
                signal: input.signal ?? null,
            });
        }
        catch (error) {
            // A cancelled request (TanStack Query, unmounted screen) stays a cancellation.
            if (error instanceof Error && (error.name === "AbortError" || error.name === "TimeoutError"))
                throw error;
            throw new ApiNetworkError(error);
        }
    }
    function refresh() {
        // One refresh at a time (refresh tokens rotate; a second concurrent use would look like reuse).
        refreshing ??= (async () => {
            try {
                const response = await send(refreshSession, { body: {} }, undefined);
                if (response.status === 401) {
                    options.onSignedOut?.();
                    return undefined;
                }
                if (!response.ok)
                    throw await apiErrorFrom(response);
                const result = authResultSchema.parse(await response.json());
                options.onRefreshed?.(result);
                return result;
            }
            finally {
                refreshing = undefined;
            }
        })();
        return refreshing;
    }
    async function call(endpoint, ...args) {
        // The typed input (CallInput<E>) is this shape; TS cannot see that through the conditional types.
        const input = (args[0] ?? {});
        if (endpoint.idempotent === true && !input.idempotencyKey) {
            throw new Error(`${endpoint.name} needs an idempotencyKey (newIdempotencyKey())`);
        }
        const sent = options.getAccessToken();
        let response = await send(endpoint, input, sent);
        if (response.status === 401 && !("public" in endpoint.access)) {
            // Another call may already have refreshed while this one was in flight: use its token.
            const current = options.getAccessToken();
            const renewed = async () => options.renewAccessToken ? await options.renewAccessToken() : (await refresh())?.accessToken;
            const token = current !== undefined && current !== sent ? current : await renewed();
            if (token === undefined)
                throw await apiErrorFrom(response);
            response = await send(endpoint, input, token);
        }
        if (!response.ok)
            throw await apiErrorFrom(response);
        if (endpoint.response === undefined || response.status === 204) {
            // Endpoints without a response schema return nothing (CallResult<E> is `undefined` for them).
            return undefined;
        }
        const json = await response.json();
        // The API validated this body against the same contract; `validateResponses` re-checks it in development.
        return (options.validateResponses === true ? endpoint.response.parse(json) : json);
    }
    return { call, refresh };
}
function pathOf(endpoint, input) {
    const params = input.params ?? {};
    const path = endpoint.path.replace(/\{(\w+)\}/g, (_match, name) => {
        const value = params[name];
        if (value === undefined)
            throw new Error(`${endpoint.name}: missing path parameter ${name}`);
        return encodeURIComponent(scalar(value));
    });
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(input.query ?? {})) {
        if (value === undefined || value === null)
            continue;
        for (const item of Array.isArray(value) ? value : [value])
            search.append(key, scalar(item));
    }
    const query = search.toString();
    return query === "" ? path : `${path}?${query}`;
}
function scalar(value) {
    if (typeof value === "string")
        return value;
    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
        return value.toString();
    }
    throw new Error(`Cannot put ${typeof value} in a URL`);
}
//# sourceMappingURL=client.js.map